import Cookies from 'js-cookie'

const TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

/**
 * 获取存储的Token
 */
export function getToken() {
    // 优先从Cookie获取，其次从localStorage
    return Cookies.get(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY)
}

/**
 * 设置Token
 */
export function setToken(token, rememberMe = false) {
    const cookieOptions = {
        secure: true,
        sameSite: 'strict',
        domain: window.location.hostname.split('.').slice(-2).join('.')  // 动态获取顶级域名
    }

    if (rememberMe) {
        // 记住登录状态，设置7天过期
        cookieOptions.expires = 7
        Cookies.set(TOKEN_KEY, token, cookieOptions)
        localStorage.setItem(TOKEN_KEY, token)
    } else {
        // 不记住登录状态，仅设置会话Cookie
        Cookies.set(TOKEN_KEY, token, cookieOptions)
        sessionStorage.setItem(TOKEN_KEY, token)
    }
}

/**
 * 移除Token
 */
export function removeToken() {
    // 清除Cookie时也要指定domain
    Cookies.remove(TOKEN_KEY, { domain: window.location.hostname.split('.').slice(-2).join('.') })
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/**
 * 获取刷新Token
 */
export function getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * 设置刷新Token
 */
export function setRefreshToken(refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

/**
 * 检查Token是否有效（未过期）
 */
export function isTokenValid(token = null) {
    const authToken = token || getToken()

    if (!authToken) {
        return false
    }

    try {
        // 解析JWT Token
        const payload = JSON.parse(atob(authToken.split('.')[1]))
        const currentTime = Math.floor(Date.now() / 1000)

        // 检查是否过期
        return payload.exp > currentTime
    } catch (error) {
        console.warn('Token解析失败:', error)
        return false
    }
}

/**
 * 获取Token中的用户信息
 */
export function getTokenPayload(token = null) {
    const authToken = token || getToken()

    if (!authToken) {
        return null
    }

    try {
        return JSON.parse(atob(authToken.split('.')[1]))
    } catch (error) {
        console.warn('Token解析失败:', error)
        return null
    }
}

/**
 * 获取Token剩余有效时间（秒）
 */
export function getTokenRemainingTime(token = null) {
    const payload = getTokenPayload(token)

    if (!payload || !payload.exp) {
        return 0
    }

    const currentTime = Math.floor(Date.now() / 1000)
    return Math.max(0, payload.exp - currentTime)
}

/**
 * 判断是否需要刷新Token（剩余时间少于5分钟）
 */
export function shouldRefreshToken(token = null) {
    const remainingTime = getTokenRemainingTime(token)
    return remainingTime > 0 && remainingTime < 300 // 5分钟 = 300秒
}

/**
 * 生成随机状态字符串（用于OAuth）
 */
export function generateState() {
    return Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15)
}

/**
 * 构建系统重定向URL
 */
export function buildRedirectUrl(systemUrl, state, additionalParams = {}) {
    const url = new URL('/login', window.location.origin)

    // 添加重定向参数
    url.searchParams.set('redirect_uri', systemUrl)
    url.searchParams.set('state', state)

    // 添加额外参数
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            url.searchParams.set(key, value)
        }
    })

    return url.toString()
}

/**
 * 解析来自系统的重定向参数
 */
export function parseRedirectParams(url = window.location.href) {
    const urlObj = new URL(url)
    const params = new URLSearchParams(urlObj.search)

    return {
        redirect_uri: params.get('redirect_uri'),
        state: params.get('state'),
        system: params.get('system'),
        client_id: params.get('client_id'),
        response_type: params.get('response_type'),
        scope: params.get('scope')
    }
}

/**
 * 构建系统回调URL
 */
export function buildCallbackUrl(redirectUri, state, token, userInfo = {}) {
    const url = new URL(redirectUri)

    // 添加认证结果
    url.searchParams.set('state', state)
    url.searchParams.set('token', token)
    url.searchParams.set('user_id', userInfo.id || '')
    url.searchParams.set('username', userInfo.username || '')

    return url.toString()
}

/**
 * 验证重定向URL是否安全
 */
export function isValidRedirectUrl(url) {
    try {
        const urlObj = new URL(url)

        // 检查协议
        if (!['http:', 'https:'].includes(urlObj.protocol)) {
            return false
        }

        // 检查是否为本地开发环境或已配置的域名
        const allowedHosts = [
            'localhost',
            '127.0.0.1',
            'auth.otidc.com',
            'system-a.otidc.com',
            'system-b.otidc.com'
        ]

        const isAllowed = allowedHosts.some(host =>
            urlObj.hostname === host || urlObj.hostname.endsWith(`.${host}`)
        )

        return isAllowed
    } catch (error) {
        return false
    }
}

/**
 * 检查当前环境是否为开发环境
 */
export function isDevelopment() {
    return process.env.NODE_ENV === 'development'
}

/**
 * 检查当前环境是否为生产环境
 */
export function isProduction() {
    return process.env.NODE_ENV === 'production'
}

/**
 * 获取当前域名
 */
export function getCurrentDomain() {
    return window.location.hostname
}

/**
 * 检查是否为移动设备
 */
export function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * 生成设备指纹（简单版本）
 */
export function generateDeviceFingerprint() {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Device fingerprint', 2, 2)

    const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage,
        canvas.toDataURL()
    ].join('|')

    // 简单哈希
    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // 转换为32位整数
    }

    return Math.abs(hash).toString(36)
} 